/**
 * What a printed order contains: the header facts a CSR reads off the order
 * screen, the standing customer notes, the line items, the Sage /C
 * instruction lines and the Sage order / CS notes. Nothing interactive.
 */
import type {
  OrderInstruction,
  PortalOrderDetailResponse,
  SageOrderNotes,
} from '../services/portalOrders';
import type { OrderLineItem, OrderRecord } from '../types/orders';
import { ORDER_STATUS_LABELS } from '../types/orders';
import { formatDate } from './dateFormatters';

export interface OrderPrintField {
  label: string;
  value: string;
}

export interface OrderPrintJob {
  order: OrderRecord;
  lineItems: OrderLineItem[];
  /** Header facts in display order; fields with no value are left out. */
  fields: OrderPrintField[];
  /** Standing Sage customer notes (AR_Customer.UDF_ORDER_NOTES). */
  customerOrderNotes: string | null;
  /** Live Sage /C comment lines, in position order. */
  instructions: string[];
  /** Sage production notes + CS note rows, when they could be loaded. */
  sageNotes: { orderNotes: string | null; csNotes: string[] } | null;
}

const BL_LABELS: Record<string, string> = {
  'ppd.': 'Prepaid',
  'col.': 'Collect',
  '3rd party': 'Third party',
};

/** One line from the ship-to JSONB the draft flow writes. */
export function formatShipToAddress(addr: Record<string, unknown> | null | undefined): string | null {
  if (!addr) return null;
  const a = addr as Record<string, string | undefined>;
  const parts = [a.ship_to_name, a.address1, a.address2, a.city, a.state, a.zip_code].filter(
    (p): p is string => !!p && p.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(', ') : null;
}

function money(val: string | null | undefined): string | null {
  if (val === null || val === undefined) return null;
  const num = parseFloat(val);
  return isNaN(num) ? null : `$${num.toFixed(2)}`;
}

function field(label: string, value: string | null | undefined): OrderPrintField | null {
  return value && value.trim() ? { label, value } : null;
}

export function buildOrderPrintFields(order: OrderRecord): OrderPrintField[] {
  const shipDate = order.requested_ship_date
    ? `${formatDate(order.requested_ship_date)}${order.requested_ship_date_confirmed ? ' (confirmed)' : ''}`
    : null;
  const salesperson = order.salesperson_name
    ? order.salesperson_no && order.salesperson_no !== order.salesperson_name
      ? `${order.salesperson_name} (${order.salesperson_no})`
      : order.salesperson_name
    : order.salesperson_no;

  return [
    field('Status', ORDER_STATUS_LABELS[order.status] ?? order.status),
    field('Sage Order #', order.sage_order_no),
    field('PO Number', order.po_number),
    field('Salesperson', salesperson),
    field('Ship To', formatShipToAddress(order.ship_to_address)),
    field('Requested Ship Date', shipDate),
    field('Ship Via', order.ship_via),
    field('Warehouse', order.warehouse_code),
    field('Bill of Lading', BL_LABELS[order.udf_bl] ?? order.udf_bl),
    field(
      'Charge Freight',
      order.charge_freight === undefined ? null : order.charge_freight ? 'Yes' : 'No (do not bill freight)',
    ),
    field('Order Type', order.order_type === 'B' ? 'Backorder' : null),
    field('Jobsite', order.udf_jobsite === 'Y' ? 'Yes' : null),
    field('Hold Reason', order.cancel_reason_code),
    field('Payment Terms', order.payment_terms),
    field('Subtotal', money(order.subtotal)),
    field('Freight', money(order.freight_cost)),
    field('Total', money(order.total)),
    field('Created', order.created_at ? formatDate(order.created_at) : null),
  ].filter((f): f is OrderPrintField => f !== null);
}

/** Instruction lines that will reach (or are in) Sage, in position order. */
export function activeInstructionLines(instructions: OrderInstruction[]): string[] {
  return [...instructions]
    .filter((i) => i.sage_state !== 'removed' && i.sage_state !== 'desk_removed')
    .sort((a, b) => a.position - b.position)
    .map((i) => i.text)
    .filter((t) => t.trim().length > 0);
}

export function buildOrderPrintJob(
  detail: PortalOrderDetailResponse,
  instructions: OrderInstruction[],
  notes: SageOrderNotes | null,
): OrderPrintJob {
  const sageNotes =
    notes && notes.available
      ? {
          orderNotes: notes.order_notes && notes.order_notes.trim() ? notes.order_notes : null,
          csNotes: notes.order_cs_note_rows
            .filter((r) => r.status !== 'archived')
            .map((r) => r.formatted_entry || [r.date_label, r.text].filter(Boolean).join(' '))
            .filter((t) => t.trim().length > 0),
        }
      : null;

  return {
    order: detail.order,
    lineItems: detail.line_items,
    fields: buildOrderPrintFields(detail.order),
    customerOrderNotes: detail.customer_order_notes?.trim() ? detail.customer_order_notes : null,
    instructions: activeInstructionLines(instructions),
    sageNotes,
  };
}
